/**
 * schema.org `Event` yapısal verisi için paylaşılan yardımcılar.
 *
 * Etkinlik ve performans sayfaları aynı `EventDetail` alanlarından JSON-LD
 * üretiyor; kurallar (tip eşlemesi, tarih biçimi, boş alanların atılması)
 * tek yerde tutulur ki iki sayfa birbirinden ayrışmasın.
 */

/** Angular `DatePipe` biçimi; `LocalizedDatePipe` saat dilimini +03:00'e sabitliyor. */
export const SCHEMA_DATE_FORMAT = "yyyy-MM-dd'T'HH:mm:ssZZZZZ";

/**
 * Yapısal veri tarihlerinde kullanılan saat dilimi.
 *
 * `LocalizedDatePipe.defaultTimezone` ile aynı değer: tüm performanslar Türkiye
 * saatiyle tanımlı ve sunucunun/tarayıcının saat dilimi JSON-LD çıktısını
 * değiştirmemeli. `LocalizedDatePipe` yerine düz `DatePipe` kullanan sayfalarda
 * (mekan, sanatçı, etkinlik grubu) bu sabit üçüncü argüman olarak verilir.
 */
export const SCHEMA_TIMEZONE = '+0300';

/**
 * Bir `ItemList` içinde yayınlanacak en fazla etkinlik sayısı.
 *
 * Mekan sayfalarında liste yüzlerce performansa çıkabiliyor; tamamını gömmek
 * HTML'i gereksiz büyütür ve Google zaten liste bütünüyle taranmak için değil,
 * sayfanın ne listelediğini anlamak için kullanır. İlk N kayıt (liste zaten
 * tarihe göre sıralı) sinyal için yeterli.
 */
export const SCHEMA_ITEM_LIST_MAX = 30;

/** `ItemList` elemanı üretmek için gereken, sayfadan bağımsız alanlar. */
export interface SchemaEventListEntry {
  /** Etkinliğin mutlak kanonik URL'i (`SeoUrlService` ile üretilir). */
  url?: string | null;
  name?: string | null;
  /** ISO 8601 biçiminde başlangıç tarihi (bkz. `SCHEMA_DATE_FORMAT`). */
  startDate?: string | null;
  venueName?: string | null;
  venueCity?: string | null;
  /** TL cinsinden en düşük fiyat (bkz. `resolveListItemPrice`). */
  price?: number;
  /** schema.org alt tipi; verilmezse jenerik `Event`. */
  eventType?: string;
}

/**
 * Liste sayfaları (etkinlik grubu, mekan, sanatçı) için `ItemList` JSON-LD'si.
 *
 * Neden `ItemList`: bu sayfalar tek bir etkinliği değil bir etkinlik kümesini
 * anlatıyor. Tek tek `Event` blokları yayınlamak sayfanın hangi etkinliğin
 * sayfası olduğu konusunda yanlış sinyal verir; `ItemList` + `ListItem.item`
 * kalıbı Google'ın etkinlik karuseli için beklediği biçimdir ve her elemanın
 * `url`'i kendi kanonik etkinlik sayfasına işaret eder.
 *
 * URL'i olmayan kayıtlar atılır: `url` olmadan liste elemanının Google
 * tarafında hiçbir değeri yok.
 *
 * @returns Yayınlanacak eleman kalmazsa `null` — çağıran `setJsonLd`'ye `null`
 *          verdiğinde varsa eski blok kaldırılır.
 */
export function buildEventItemList(
  listName: string,
  entries: SchemaEventListEntry[],
  currency = 'TRY',
): Record<string, unknown> | null {
  const listElements = (entries ?? [])
    .filter((entry) => !!entry?.url)
    .slice(0, SCHEMA_ITEM_LIST_MAX)
    .map((entry, index) =>
      pruneEmpty<Record<string, unknown>>({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': entry.eventType ?? 'Event',
          url: entry.url,
          name: entry.name,
          startDate: entry.startDate,
          eventStatus: 'https://schema.org/EventScheduled',
          eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
          location: {
            '@type': 'Place',
            name: entry.venueName,
            address: {
              '@type': 'PostalAddress',
              addressLocality: entry.venueCity,
              addressCountry: 'TR',
            },
          },
          offers:
            entry.price === undefined
              ? undefined
              : {
                  '@type': 'Offer',
                  url: entry.url,
                  price: entry.price,
                  priceCurrency: currency,
                  availability: 'https://schema.org/LimitedAvailability',
                },
        },
      }),
    );

  if (listElements.length === 0) {
    return null;
  }

  return pruneEmpty<Record<string, unknown>>({
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: listName,
    numberOfItems: listElements.length,
    itemListElement: listElements,
  });
}

/**
 * Biletix ana kategori kodu → schema.org alt tipi.
 *
 * Kodlar başlık menüsündeki `/category/{KOD}/...` link'lerinden alındı
 * (`event-category-mobile-menu.component.ts`): MUSIC, ART, SPORT, FAMILY, OTHER.
 *
 * ÖNEMLİ: `@type` daha önce `eventDetail.eventCategory` ile dolduruluyordu, yani
 * dile göre değişen görünen ad ("Müzik", "Spor") schema.org tipi olarak
 * yayınlanıyordu. Böyle bir tip yok; Google bu bloğu tanınmayan tip olarak
 * eler. Eşleme bu yüzden görünen ad üzerinden değil, kod üzerinden yapılır.
 */
const SCHEMA_EVENT_TYPES: Record<string, string> = {
  MUSIC: 'MusicEvent',
  ART: 'TheaterEvent',
  SPORT: 'SportsEvent',
  FAMILY: 'ChildrensEvent',
  OTHER: 'Event',
};

/**
 * HTML içerebilen açıklama alanlarını düz metne çevirir.
 *
 * Mekan (`venueDescription`) ve sanatçı (`artistDescription`) açıklamaları
 * editörden HTML olarak geliyor. JSON-LD `description` alanı düz metin olmak
 * zorunda; etiketler olduğu gibi gönderilirse Google açıklamayı işaretleme
 * artığıyla birlikte gösterir.
 */
export function toPlainText(html?: string | null, maxLength = 500): string | undefined {
  if (!html) {
    return undefined;
  }

  const text = html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\s+/g, ' ')
    .trim();

  if (!text) {
    return undefined;
  }
  return text.length > maxLength ? `${text.slice(0, maxLength).trimEnd()}…` : text;
}

/** Kategori kodu bilinmiyorsa jenerik `Event` kullanılır — geçerli bir tiptir. */
export function schemaEventType(eventCategoryCode?: string | null): string {
  if (!eventCategoryCode) {
    return 'Event';
  }
  return SCHEMA_EVENT_TYPES[eventCategoryCode.trim().toUpperCase()] ?? 'Event';
}

/**
 * Nesneden `undefined`, `null` ve boş string değerleri özyinelemeli olarak atar.
 *
 * Eski JSON-LD `sameAs: ''`, `postalCode: ''`, `streetAddress: ''` gibi boş
 * alanlar yayınlıyordu; Google bunları eksik değer değil, geçersiz değer olarak
 * raporluyor. Alanı hiç göndermemek doğru davranış.
 */
export function pruneEmpty<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .map((item) => pruneEmpty(item))
      .filter((item) => !isBlank(item)) as unknown as T;
  }

  if (isPlainObject(value)) {
    const result: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>).forEach(([key, raw]) => {
      const pruned = pruneEmpty(raw);
      if (!isBlank(pruned)) {
        result[key] = pruned;
      }
    });
    return result as unknown as T;
  }

  return value;
}

function isBlank(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim() === '';
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  if (isPlainObject(value)) {
    return Object.keys(value as Record<string, unknown>).length === 0;
  }
  return false;
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}
